import type { ClawPassProduct, ClawPassType } from '@/app/types';
import type { ClawPassPlanType } from './constants';

/**
 * 创作者计划捆绑套餐总价计算（单位：分）。
 * 规则：第一份按 product.price 计费，其余份数按 original_price 计费。
 * 是否享受首购优惠由后端的 product.price 字段反映：
 * - 命中首购：price = 优惠价 → 第一份打折，后续份原价
 * - 未命中首购：price = original_price → 全部按原价
 */
export const computeClawPassTotalCents = (
  product: Pick<ClawPassProduct, 'price' | 'original_price'>,
  quantity: number,
): number => {
  const safeQuantity = Math.max(1, quantity);
  const originalPrice = product.original_price ?? product.price;
  return product.price + originalPrice * (safeQuantity - 1);
};

/**
 * 安全的「加 N 个月」：避免 setMonth 在月末溢出。
 * 例如 1/31 + 1 个月，朴素 setMonth 会变成 3/3（2 月没有 31 号），
 * 这里回退为目标月的最后一天（2/28 或 2/29）。
 */
export const addMonths = (base: Date, months: number): Date => {
  const d = new Date(base);
  const targetMonth = d.getMonth() + months;
  d.setMonth(targetMonth);
  // 若 getMonth() 不等于规范化后的目标月（说明溢出到了下一个月），回退到上月最后一天
  if (d.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    d.setDate(0);
  }
  return d;
};

export interface ClawAdvanceWindow {
  /** 当前权益到期时间（= 本次购买的预计生效时间） */
  currentExpire: Date;
  /** 本次购买预计生效时间（与 currentExpire 相同，语义化别名） */
  effectiveStart: Date;
  /** 本次购买预计到期时间（currentExpire 顺延 quantity 个月） */
  effectiveEnd: Date;
}

/**
 * Claw 权益包「叠加续购」生效窗口计算。
 *
 * 续购语义：用户当前已持有生效中的 claw 权益（has_benefit 且非 trial），
 * 本次购买不立即生效，而是在当前权益到期（valid_to）后顺延 quantity 个月。
 *
 * @param validTo 当前权益到期时间（ISO 字符串，通常来自 clawPass.valid_to）
 * @param quantity 本次购买时长（月），至少按 1 计
 * @returns 计算窗口；validTo 缺失或非法时返回 null
 */
export const computeClawAdvanceWindow = (
  validTo: string | undefined | null,
  quantity: number,
): ClawAdvanceWindow | null => {
  if (!validTo) return null;
  const currentExpire = new Date(validTo);
  if (Number.isNaN(currentExpire.getTime())) return null;
  const months = Math.max(1, Math.floor(quantity));
  return {
    currentExpire,
    effectiveStart: currentExpire,
    effectiveEnd: addMonths(currentExpire, months),
  };
};

/**
 * 取 claw 权益的「最终有效期到期时间」。
 *
 * 后端 active_benefit_periods 是已排序的所有生效订阅周期，第一项是最终到期；
 * 续购叠加场景下，最终到期 > clawPass.valid_to（当前周期到期）。
 *
 * 用户视角的「我的权益什么时候到期」始终指最终到期：
 * - banner / popover 倒计时：基于最终到期，叠加续购后倒计时自动延长
 * - 续购订单确认信息：当前套餐有效期 = 最终到期
 *
 * 优先返回 active_benefit_periods 第一项的 valid_to；缺失或数组为空时回退到顶层 valid_to。
 */
export const getEffectiveValidTo = (
  clawPass: Pick<ClawPassType, 'valid_to' | 'active_benefit_periods'> | null | undefined,
): string | undefined => {
  const periods = clawPass?.active_benefit_periods;
  if (periods && periods.length > 0) {
    const firstValidTo = periods[0]?.valid_to;
    if (firstValidTo) return firstValidTo;
  }
  return clawPass?.valid_to;
};

export interface ClawPassProductGroup {
  /** 同一档位（basic / pro），可能包含 1-2 个商品（claw_pass / claw_pass_bundle） */
  key: ClawPassPlanType;
  products: ClawPassProduct[];
}

const PLAN_ORDER: ClawPassPlanType[] = ['claw_pass_basic', 'claw_pass_pro'];

/**
 * 按 claw_pass_plan_type 分组同一档位下的商品（claw_pass + claw_pass_bundle）。
 *
 * 后端约定：可同时返回两种 product_type；若只返回一种则不显示切换器，
 * 由 PassCard 内部处理。组顺序固定为 basic → pro，避免 productList 顺序变动导致卡片位置抖动。
 */
export const groupClawPassProducts = (products: ClawPassProduct[]): ClawPassProductGroup[] => {
  const groupMap = new Map<ClawPassPlanType, ClawPassProduct[]>();
  for (const product of products) {
    const planType = product.claw_pass_plan_type as ClawPassPlanType | undefined;
    if (!planType || !PLAN_ORDER.includes(planType)) continue;
    const existing = groupMap.get(planType);
    if (existing) {
      existing.push(product);
    } else {
      groupMap.set(planType, [product]);
    }
  }

  return PLAN_ORDER.filter((key) => groupMap.has(key)).map((key) => ({
    key,
    products: groupMap.get(key) ?? [],
  }));
};
